import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Bot, CheckCircle2, Download, ExternalLink, RefreshCw, XCircle } from "lucide-react";
import {
  checkPublishedLlmsFiles,
  downloadText,
  generateLlmsBundle,
  type LlmsFileStatus,
} from "@/lib/llms";
import type { LlmsBundle, LlmsData } from "@/lib/llmsContent";

type FileName = keyof LlmsBundle;

const FILES: FileName[] = ["llms.txt", "llms-full.txt", "llms-en.txt", "llms-full-en.txt"];

const LlmsAdmin = () => {
  const { toast } = useToast();
  const [bundle, setBundle] = useState<LlmsBundle | null>(null);
  const [data, setData] = useState<LlmsData | null>(null);
  const [status, setStatus] = useState<LlmsFileStatus[]>([]);
  const [active, setActive] = useState<FileName>("llms.txt");
  const [loading, setLoading] = useState(false);

  const regenerate = async () => {
    setLoading(true);
    try {
      const [gen, published] = await Promise.all([generateLlmsBundle(), checkPublishedLlmsFiles()]);
      setBundle(gen.bundle);
      setData(gen.data);
      setStatus(published);
      toast({ title: "Base de conhecimento regenerada a partir do catálogo ao vivo" });
    } catch (e) {
      toast({ title: "Erro ao gerar", description: (e as Error).message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = bundle?.[active] || "";

  return (
    <div className="space-y-6">
      <Card className="p-5 border border-border">
        <div className="flex flex-wrap items-center gap-3">
          <Bot className="h-5 w-5 text-accent" />
          <div className="min-w-0">
            <h2 className="font-display text-lg font-bold text-foreground">IA / LLMs</h2>
            <p className="font-body text-xs text-muted-foreground">
              Arquivos de conhecimento para ChatGPT, Gemini, Claude, Perplexity e Copilot. São
              regenerados automaticamente a cada publicação do site, sempre a partir dos imóveis,
              lançamentos e artigos ativos.
            </p>
          </div>
          <Button className="ml-auto" size="sm" variant="outline" onClick={regenerate} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Regenerar
          </Button>
        </div>

        {data && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 font-body text-sm">
            <div className="rounded-md border border-border p-3">
              <p className="text-muted-foreground text-xs">Imóveis indexados</p>
              <p className="font-display text-xl font-bold">{data.listings.length}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-muted-foreground text-xs">Lançamentos</p>
              <p className="font-display text-xl font-bold">{data.launches.length}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-muted-foreground text-xs">Artigos</p>
              <p className="font-display text-xl font-bold">{data.posts.length}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-muted-foreground text-xs">Última geração</p>
              <p className="font-body text-sm font-semibold">
                {new Date(data.generatedAt!).toLocaleString("pt-BR")}
              </p>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5 border border-border">
        <h3 className="font-display text-base font-bold mb-3">Arquivos publicados</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {status.map((f) => (
            <div key={f.name} className="rounded-md border border-border p-3 flex items-start gap-3">
              {f.ok ? (
                <CheckCircle2 className="h-4 w-4 text-accent mt-0.5" />
              ) : (
                <XCircle className="h-4 w-4 text-destructive mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-body text-sm font-semibold truncate">/{f.name}</p>
                <p className="font-body text-xs text-muted-foreground">
                  {f.ok
                    ? `${(f.size / 1024).toFixed(1)} KB · ${f.headings} seções · ${f.links} links`
                    : "Não disponível — publique o site para gerar"}
                </p>
                {f.lastModified && (
                  <p className="font-body text-[11px] text-muted-foreground">
                    Gerado em {new Date(f.lastModified).toLocaleString("pt-BR")}
                  </p>
                )}
              </div>
              <Button asChild size="sm" variant="ghost">
                <a href={f.url} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border border-border">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {FILES.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setActive(name)}
              className={`px-3 py-1.5 rounded-md text-xs font-body border transition-colors ${
                active === name
                  ? "bg-accent text-accent-foreground border-accent font-semibold"
                  : "bg-card text-muted-foreground border-border hover:border-accent/50"
              }`}
            >
              {name}
            </button>
          ))}
          <Badge variant="outline" className="font-body text-[10px]">
            {(preview.length / 1024).toFixed(1)} KB
          </Badge>
          <Button
            className="ml-auto"
            size="sm"
            variant="outline"
            disabled={!preview}
            onClick={() => downloadText(active, preview)}
          >
            <Download className="h-4 w-4" /> Exportar
          </Button>
        </div>
        <Textarea
          readOnly
          value={preview}
          className="font-mono text-xs h-[420px]"
          placeholder="Gerando pré-visualização…"
        />
      </Card>
    </div>
  );
};

export default LlmsAdmin;
